// #region Imports
import BadgeIcon from "@mui/icons-material/Badge";
import MessageIcon from "@mui/icons-material/Message";
import { styled } from "@mui/material";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Container from "@mui/material/Container";
import Divider from "@mui/material/Divider";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { skipToken } from "@reduxjs/toolkit/query/react";
import Loader from "_shared/_views/loading/loader";
import Guid from "_shared/guids/guid";
import TeamAvatar from "marketplace/_views/avatars/team-avatar";
import { useGetTeamByIdQuery } from "marketplace/teams/_api/marketplace-team-api";
import {
  getTeamById
} from "marketplace/teams/_store/marketplace-teams-redux-slice";
import ViewTeam from "marketplace/teams/_view/view-team";
import ChatWindow from "messaging/forums/_views/components/forum-chat-window";
import { enqueueSnackbar } from "notistack";
import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useGetChatForumForNetworkConnectionQuery } from "users/networking/connections/_api/user-network-connection-api";
import { selectIsNetworkedWith } from "users/networking/connections/_selectors/user-network-connection-selectors";
import NetworkButton from "users/networking/connections/_views/components/network-button";
import TeamProfile from "../team-profile";
import Forum from "messaging/forums/forum";
// #endregion

// #region Styled Components
const MainContainer = styled("div")(({ theme }) => ({
  display: "grid",
  gridTemplateRows: "auto 1fr",
  paddingBottom: theme.spacing(8),
  width: "100%",
}));
const HeaderContainer = styled("div")(({ theme }) => ({
  [theme.breakpoints.down("lg")]: {
    padding: theme.spacing(3, 0),
  },
  alignItems: "center",
  backgroundColor: theme.palette.background.paper,
  display: "flex",
  flexDirection: "column",
  gridColumn: "1 / auto",
  gridRowStart: "1",
  justifyContent: "center",
  padding: theme.spacing(3),
  paddingTop: theme.spacing(1),
  position: "sticky",
  top: 0,
  width: "100%",
  zIndex: 10,
}));
const StyledAvatar = styled(TeamAvatar)(({ theme }) => ({
  [theme.breakpoints.down("sm")]: {
    height: theme.spacing(7),
    width: theme.spacing(7),
  },
  height: theme.spacing(15),
  width: theme.spacing(15),
}));
const ChatContainer = styled(Container)(({ theme }) => ({
  [theme.breakpoints.down("sm")]: {
    padding: 0,
  },
  [theme.breakpoints.up("md")]: {
    padding: theme.spacing(0, 1),
  },
  [theme.breakpoints.up("lg")]: {
    padding: theme.spacing(0, 4),
  },
  gridColumn: "1 / auto",
  gridRowStart: "2",
  gridRowEnd: "none",
}));
const HeaderInnerContainer = styled("div")(({ theme }) => ({
  [theme.breakpoints.down("sm")]: {
    justifyItems: "center",
  },
  [theme.breakpoints.up("sm")]: {
    gridTemplateColumns: "min-content auto",
    gridTemplateRows: "min-content auto",
  },
  [theme.breakpoints.up("lg")]: {
    gridTemplateColumns: "min-content auto 1fr",
  },
  alignItems: "flex-start",
  gap: theme.spacing(2),
  display: "grid",
  width: "100%",
}));
const TitleContainer = styled("div")(({ theme }) => ({
  [theme.breakpoints.down("sm")]: {
    alignItems: "center",
    justifyContent: "center",
    width: "100%",
  },
  display: "flex",
  flexDirection: "column",
  height: "100%",
}));
const ButtonContainer = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "row",
  justifyContent: "flex-start",
  marginTop: theme.spacing(1),
  "& > *:not(:last-child)": {
    marginRight: theme.spacing(2),
  },
}));
const ProfileTypeChip = styled(Chip)(({ theme }) => ({
  color: theme.palette.text.disabled,
  margin: theme.spacing(0.25),
}));
const NameChipContainer = styled("div")(({ theme }) => ({
  [theme.breakpoints.down("sm")]: {
    alignItems: "center",
    justifyContent: "center",
    width: "100%",
  },
  alignItems: "flex-start",
  display: "flex",
  flex: 1,
  flexDirection: "column",
  justifyContent: "flex-start",
}));
const DescriptionContainer = styled("div")(({ theme }) => ({
  [theme.breakpoints.down("sm")]: {
    alignItems: "center",
    justifyContent: "center",
    margin: theme.spacing(1, 0),
    width: "100%",
  },
  [theme.breakpoints.up("sm")]: {
    gridColumn: "1 / span 2",
    gridRow: "2",
    margin: theme.spacing(2, 0),
  },
  [theme.breakpoints.up("lg")]: {
    gridColumn: "3",
    gridRow: "1",
    margin: 0,
  },
  display: "flex",
  flexDirection: "column",
  height: "100%",
  justifyContent: "space-between",
  margin: 0,
  textAlign: "left",
  width: "100%",
}));
const DescriptionText = styled(Typography)(({ theme }) => ({
  [theme.breakpoints.down("sm")]: {
    textAlign: "center",
  },
  fontSize: "1.1em",
  fontStyle: "italic",
  lineHeight: "1.1em",
}));
const DescriptionDivider = styled(Divider)(({ theme }) => ({
  marginTop: theme.spacing(1),
  width: "100%",
}));
const MessageButtonContainer = styled("span")<
  { networked: boolean }>((
    { networked: inNetwork }) => ({
      cursor: inNetwork ? "pointer" : "not-allowed",
    }));
// #endregion

// #region Component Input Props
export type ViewTeamProfileProps = {
  teamId: Guid;
  profile?: TeamProfile;
};

export default function ViewTeamProfile(props: Readonly<ViewTeamProfileProps>) {
  const { teamId } = props;

  // #region State
  const [chatOpen, setChatOpen] = useState<boolean>(false);

  // #region Hooks

  const teamQuery = useGetTeamByIdQuery({ teamId: teamId.value });

  // #region Selectors
  const team = useSelector(getTeamById(teamId));
  const { isLoading: isLoadingTeam, error: errorLoadingTeam } = teamQuery;
  const isNetworkedWithUser = useSelector(selectIsNetworkedWith(team?.profile.contactUserId));
  const forumQuery = useGetChatForumForNetworkConnectionQuery(
    team?.profile?.contactUserId ? { userId: team?.profile.contactUserId } : skipToken
  );

  const forum = useMemo(() => {
    if (!forumQuery.data) return null;
    return Forum.fromJSON(forumQuery.data);
  }, [forumQuery.data]);

  // #region Lifecycle Hooks


  useEffect(() => {
    if (errorLoadingTeam) {
      enqueueSnackbar("Couldn't load team profile.", { variant: "warning" });
    }
  }, [errorLoadingTeam]);
  // #endregion

  // #region Render
  return (
    <MainContainer>
      <HeaderContainer>
        <HeaderInnerContainer>
          <StyledAvatar
            team={team}
          />
          <TitleContainer>
            <NameChipContainer>
              <Typography variant="h4">{team?.profile?.name}</Typography>
              <span>
                {team?.profile?.categories?.map((category) => (
                  <ProfileTypeChip key={category} label={category} size="small" />
                ))}
              </span>
            </NameChipContainer>
            <ButtonContainer>
              {team?.profile?.contactUserId && <NetworkButton userId={team?.profile?.contactUserId} />}
              <Tooltip
                title={(function () {
                  if (chatOpen) return "Back to profile";
                  if (isNetworkedWithUser) return "Send message";
                  return "You must be in the user's network to send a message";
                })()}
              >
                <MessageButtonContainer networked={isNetworkedWithUser}>
                  <Button
                    variant="contained"
                    startIcon={chatOpen ? <BadgeIcon /> : <MessageIcon />}
                    disableElevation
                    disabled={!isNetworkedWithUser}
                    color="primary"
                    onClick={() => setChatOpen((prevValue) => !prevValue)}
                  >
                    {chatOpen ? "Back to profile" : "Message"}
                  </Button>
                </MessageButtonContainer>
              </Tooltip>
            </ButtonContainer>
          </TitleContainer>
          <DescriptionContainer>
            <DescriptionText variant="subtitle1">{team?.profile?.description}</DescriptionText>
            <DescriptionDivider />
          </DescriptionContainer>
        </HeaderInnerContainer>
      </HeaderContainer>
      <ChatContainer>
        {isLoadingTeam && <Loader />}
        {chatOpen && forumQuery && (
          <ChatWindow forums={forum ? [forum] : []} />
        )}
      </ChatContainer>
      {!chatOpen && !isLoadingTeam && (
        <ViewTeam team={team} />
      )}
    </MainContainer>
  );
}
// #endregion
